<template>
    <div class="mode-card">
        <div class="card">
            <!-- Header with Lottie -->
            <div class="card-title">
                <div class="lottie-header" ref="lottieHeaderRef" />
                <span class="card-label">{{ title }}</span>
            </div>

            <!-- Loader -->
            <div v-if="isLoading" class="lottie-loader" ref="lottieLoaderRef" />

            <!-- List -->
            <transition name="fade">
                <div v-if="!isLoading" class="card-list">
                    <div v-for="(item, index) in limitedItems" :key="index" class="list-row">
                        <img :src="getIconForService(item.name)" class="logo" />
                        <div class="info">
                            <span class="name">{{ item.name }}</span>
                        </div>
                        <div class="time">🚶 {{ item.walkTime }} min</div>
                    </div>
                    <div v-if="limitedItems.length === 0" class="no-data">No data available</div>
                </div>
            </transition>
        </div>
    </div>
</template>

<script setup>
import { ref, computed, onMounted, defineProps } from 'vue'
import lottie from 'lottie-web'

// SVG icons (imported directly) - Complete set
import bike from './commuteIcons/bike.svg'
import bolt from './commuteIcons/bolt.svg'
import booknDrive from './commuteIcons/booknDrive.svg'
import callABike from './commuteIcons/callABike.svg'
import cambio from './commuteIcons/cambio.svg'
import deer from './commuteIcons/deer.svg'
import dott from './commuteIcons/dott.svg'
import free2move from './commuteIcons/free2move.svg'
import freenow from './commuteIcons/freenow.svg'
import kvb from './commuteIcons/KVB.svg'
import lime from './commuteIcons/lime.svg'
import limeBike from './commuteIcons/limeBike.svg'
import miles from './commuteIcons/miles.svg'
import mvg from './commuteIcons/mvg.svg'
import nextbike from './commuteIcons/nextbike.svg'
import regioRadStuttgart from './commuteIcons/regioRadStuttgart.svg'
import sbahn from './commuteIcons/sbahn.svg'
import shareNow from './commuteIcons/shareNow.svg'
import sixt from './commuteIcons/sixt.svg'
import stadtrad from './commuteIcons/stadtrad.svg'
import taxide from './commuteIcons/taxide.svg'
import tier from './commuteIcons/tier.svg'
import ubahn from './commuteIcons/ubahn.svg'
import uber from './commuteIcons/uber.svg'
import voi from './commuteIcons/voi.svg'
import walk from './commuteIcons/walk.svg'

// Lottie animations
import lottieCar from './commuteAnimations/lottie_car.json'
import lottieTaxi from './commuteAnimations/lottie_taxi.json'
import lottieEscooter from './commuteAnimations/lottie_escooter.json'
import lottieCycle from './commuteAnimations/lottie_cycle.json'

const props = defineProps({
    title: String,
    mode: String,
    items: Array,
    isLoading: Boolean
})

const lottieHeaderRef = ref(null)
const lottieLoaderRef = ref(null)

const limit = computed(() => {
    switch (props.mode) {
        case 'CAR SHARING': return 4
        case 'TAXI SERVICE': return 3
        case 'SCOOTER SHARING': return 4
        case 'BIKE SHARING': return 1
        default: return 5
    }
})

const limitedItems = computed(() => props.items.slice(0, limit.value))

// Complete icon mapping that matches the service names
const iconMap = {
    // Car sharing services
    'sixt': sixt,
    'free2move': free2move,
    'miles': miles,
    'cambio': cambio,
    'bookndrive': booknDrive,
    'booknDrive': booknDrive,
    'shareNow': shareNow,

    // Taxi services
    'uber': uber,
    'freenow': freenow,
    'taxide': taxide,

    // Scooter services
    'dott': dott,
    'voi': voi,
    'lime': lime,
    'bolt': bolt,
    'tier': tier,

    // Bike services
    'stadtrad': stadtrad,
    'nextbike': nextbike,
    'mvg': mvg,
    'regioRadStuttgart': regioRadStuttgart,
    'regioradstuttgart': regioRadStuttgart,
    'kvb': kvb,
    'callABike': callABike,
    'limeBike': limeBike,
    'bike': bike,

    // Additional/fallback
    'deer': deer,
    'walk': walk,
    'sbahn': sbahn,
    'ubahn': ubahn
}

// Function to get icon for a service name
const getIconForService = (serviceName) => {
    if (!serviceName) return walk

    // Try exact match first
    if (iconMap[serviceName]) {
        return iconMap[serviceName]
    }

    // Try lowercase
    const lowerName = serviceName.toLowerCase()
    if (iconMap[lowerName]) {
        return iconMap[lowerName]
    }

    // Try removing spaces and special characters
    /* eslint-disable */
    const cleanName = serviceName.toLowerCase().replace(/[\s\-\.]+/g, '')
    if (iconMap[cleanName]) {
        return iconMap[cleanName]
    }

    // Try partial matches for common service patterns
    const partialMatches = {
        'sixt': sixt,
        'free2move': free2move,
        'miles': miles,
        'cambio': cambio,
        'bookn': booknDrive,
        'book': booknDrive,
        'drive': booknDrive,
        'share': shareNow,
        'uber': uber,
        'free': freenow,
        'now': freenow,
        'taxi': taxide,
        'dott': dott,
        'voi': voi,
        'lime': lime,
        'bolt': bolt,
        'tier': tier,
        'stadt': stadtrad,
        'rad': stadtrad,
        'next': nextbike,
        'bike': nextbike,
        'mvg': mvg,
        'regio': regioRadStuttgart,
        'stuttgart': regioRadStuttgart,
        'kvb': kvb,
        'call': callABike
    }

    for (const [key, icon] of Object.entries(partialMatches)) {
        if (cleanName.includes(key)) {
            return icon
        }
    }

    // Fallback to walk icon
    return walk
}

const getLottie = () => {
    switch (props.mode) {
        case 'CAR SHARING': return lottieCar
        case 'TAXI SERVICE': return lottieTaxi
        case 'SCOOTER SHARING': return lottieEscooter
        case 'BIKE SHARING': return lottieCycle
        default: return null
    }
}

onMounted(() => {
    const anim = getLottie()
    if (anim) {
        if (lottieHeaderRef.value) {
            lottie.loadAnimation({
                container: lottieHeaderRef.value,
                renderer: 'svg',
                loop: true,
                autoplay: true,
                animationData: anim
            })
        }

        if (lottieLoaderRef.value) {
            lottie.loadAnimation({
                container: lottieLoaderRef.value,
                renderer: 'svg',
                loop: true,
                autoplay: true,
                animationData: anim
            })
        }
    }
})
</script>

<style scoped>
.mode-card {
    width: 100%;
    max-width: 400px;
    margin-bottom: 10px;
}

.card {
    background: linear-gradient(135deg, #ffffff 0%, #f9fafb 100%);
    border-radius: 14px;
    border: 1px solid rgba(226, 232, 240, 0.5);
    box-shadow:
        0 4px 16px rgba(0, 0, 0, 0.06),
        0 1px 4px rgba(0, 0, 0, 0.04);
    padding: 10px 12px;
    display: flex;
    flex-direction: column;
    gap: 6px;
    transition: all 0.2s ease;
    position: relative;
}

.card::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 2px;
    background: linear-gradient(90deg, #8b5cf6, #06b6d4, #f59e0b);
    border-radius: 14px 14px 0 0;
    opacity: 0.6;
}

.card:hover {
    transform: translateY(-1px);
    box-shadow:
        0 8px 24px rgba(0, 0, 0, 0.1),
        0 4px 8px rgba(0, 0, 0, 0.06);
}

.card-title {
    display: flex;
    align-items: center;
    gap: 8px;
    margin-bottom: 4px;
}

.card-label {
    font-size: 16px;
    font-weight: 600;
    color: #1e293b;
    letter-spacing: -0.025em;
}

.lottie-header {
    width: 24px;
    height: 24px;
    opacity: 0.9;
    filter: drop-shadow(0 1px 2px rgba(0, 0, 0, 0.1));
}

.lottie-loader {
    display: flex;
    justify-content: center;
    align-items: center;
    height: 60px;
}

.card-list {
    display: flex;
    flex-direction: column;
    gap: 1px;
}

.list-row {
    display: flex;
    align-items: center;
    padding: 6px 4px;
    font-size: 14px;
    border-bottom: 1px solid rgba(226, 232, 240, 0.3);
    border-radius: 6px;
    transition: all 0.2s ease;
    position: relative;
}

.list-row:last-child {
    border-bottom: none;
}

.list-row:hover {
    background: rgba(248, 250, 252, 0.8);
    border-bottom-color: rgba(59, 130, 246, 0.2);
    transform: translateX(2px);
}

.logo {
    width: 18px;
    height: 18px;
    object-fit: contain;
    margin-right: 8px;
    padding: 2px;
    background: rgba(248, 250, 252, 0.8);
    border-radius: 4px;
    border: 1px solid rgba(226, 232, 240, 0.4);
}

.info {
    flex-grow: 1;
}

.name {
    font-size: 14px;
    color: #374151;
    font-weight: 600;
    line-height: 1.2;
}

.time {
    font-size: 12px;
    color: #059669;
    font-weight: 600;
    background: rgba(16, 185, 129, 0.1);
    padding: 1px 4px;
    border-radius: 4px;
    border: 1px solid rgba(16, 185, 129, 0.2);
    white-space: nowrap;
}

.no-data {
    font-size: 14px;
    color: #94a3b8;
    text-align: center;
    padding: 15px 0;
    font-weight: 500;
}

.fade-enter-active,
.fade-leave-active {
    transition: opacity 0.3s ease;
}

.fade-enter-from,
.fade-leave-to {
    opacity: 0;
}

@media (max-width: 768px) {
    .card {
        padding: 8px 10px;
    }

    .card-label {
        font-size: 14px;
    }

    .name {
        font-size: 13px;
    }

    .time {
        font-size: 11px;
    }

    .logo {
        width: 16px;
        height: 16px;
    }

    .lottie-header {
        width: 20px;
        height: 20px;
    }

    .lottie-loader {
        height: 50px;
    }

    .list-row {
        font-size: 13px;
        padding: 5px 3px;
    }

    .no-data {
        font-size: 13px;
        padding: 12px 0;
    }
}

@media (max-width: 480px) {
    .card-label {
        font-size: 13px;
    }

    .name {
        font-size: 12px;
    }

    .time {
        font-size: 10px;
        padding: 1px 3px;
    }

    .list-row {
        font-size: 12px;
        padding: 4px 2px;
    }

    .no-data {
        font-size: 12px;
        padding: 10px 0;
    }

    .logo {
        width: 14px;
        height: 14px;
    }
}
</style>